import type { DocumentHeader } from "./types.js";

/**
 * Rendering helpers shared by every template.
 *
 * Formatting lives here rather than in each template so two documents never
 * disagree about how many decimal places a heat loss carries — a discrepancy
 * between the design pack and the heat loss report would be exactly the kind of
 * thing an assessor picks up on.
 */

/** Escapes text for HTML. Every value from a survey passes through this. */
export function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Fixed-decimal formatting that does not depend on locale.
 *
 * `toLocaleString` would render 1234.5 as "1,234.5" or "1.234,5" depending on
 * the machine, which would make output non-deterministic across environments —
 * and the document engine hashes this output.
 */
export function num(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return "—";
  const fixed = value.toFixed(decimals);
  const [whole, fraction] = fixed.split(".");
  const grouped = whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction ? `${grouped}.${fraction}` : grouped;
}

export const watts = (w: number) => `${num(w, 0)} W`;
export const wattsPerM2 = (w: number) => `${num(w, 1)} W/m²`;
export const degrees = (c: number) => `${num(c, 1)} °C`;
export const litres = (l: number) => `${num(l, 0)} litres`;
export const squareMetres = (m2: number) => `${num(m2, 2)} m²`;

/** Renders an ISO date as e.g. "25 August 2026", without locale dependence. */
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function isoDateToLong(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const day = Number(m[3]);
  const month = MONTHS[Number(m[2]) - 1] ?? "";
  return `${day} ${month} ${m[1]}`;
}

export interface TableColumn<T> {
  readonly heading: string;
  readonly cell: (row: T) => string;
  /** Numeric columns are right-aligned and use tabular figures. */
  readonly numeric?: boolean;
}

export function table<T>(
  columns: readonly TableColumn<T>[],
  rows: readonly T[],
  opts: { footer?: readonly string[]; caption?: string } = {},
): string {
  const head = columns
    .map((c) => `<th${c.numeric ? ' class="n"' : ""}>${esc(c.heading)}</th>`)
    .join("");
  const body = rows
    .map(
      (row) =>
        `<tr>${columns
          .map((c) => `<td${c.numeric ? ' class="n"' : ""}>${c.cell(row)}</td>`)
          .join("")}</tr>`,
    )
    .join("\n");
  const foot = opts.footer
    ? `<tfoot><tr>${opts.footer
        .map((cell, i) =>
          `<td${columns[i]?.numeric ? ' class="n"' : ""}>${cell}</td>`,
        )
        .join("")}</tr></tfoot>`
    : "";
  const caption = opts.caption
    ? `<caption>${esc(opts.caption)}</caption>`
    : "";
  return `<table>${caption}<thead><tr>${head}</tr></thead><tbody>\n${body}\n</tbody>${foot}</table>`;
}

/** A labelled field list — the shape most of the compliance items take. */
export function fieldList(
  entries: readonly (readonly [string, string])[],
): string {
  return `<dl>${entries
    .map(([k, v]) => `<dt>${esc(k)}</dt><dd>${v}</dd>`)
    .join("")}</dl>`;
}

const STYLES = `
:root { --ink:#1a1a1a; --muted:#5a5a5a; --line:#c8ccce; --tint:#f2f5f6; }
* { box-sizing: border-box; }
/*
 * ARIAL, 11pt -- the Normal style of the company letterhead
 * (BYECO Letter Head.docx: w:rFonts Arial, w:sz 22 half-points). A document a
 * customer signs should look like the paper the same company sends them, and
 * it did not: the stack began with the reader's own system font and reached
 * Arial only as a fourth fallback, so one contract rendered differently on
 * every machine that opened it.
 *
 * LIBERATION SANS IS SECOND, FOR THE SERVER. The PDF renderer runs in a Linux
 * container with no Arial, and Liberation Sans is metric-compatible with it --
 * the same advance widths, so lines and pages break identically. A customer on
 * Windows reads Arial and the PDF kept in the vault is laid out the same,
 * which is the property that matters when the two are compared later.
 */
body { font-family: Arial, "Liberation Sans", Helvetica, sans-serif;
  color: var(--ink); line-height: 1.45; margin: 0; padding: 24px; font-size: 11pt; }
h1 { font-size: 18pt; margin: 0 0 4px; }
h2 { font-size: 13pt; margin: 20px 0 6px; padding-bottom: 3px;
  border-bottom: 1px solid var(--line); }
h3 { font-size: 11.5pt; margin: 14px 0 4px; }
.doc-header { border-bottom: 2px solid var(--ink); padding-bottom: 10px; margin-bottom: 6px; }
.doc-meta { display: flex; flex-wrap: wrap; gap: 4px 28px; color: var(--muted);
  font-size: 10pt; margin-top: 6px; }
.doc-meta b { color: var(--ink); font-weight: 600; }
.standard { font-size: 9.5pt; color: var(--muted); margin: 2px 0 0; }
table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 10.5pt; }
caption { text-align: left; font-weight: 600; padding-bottom: 4px; }
th, td { border: 1px solid var(--line); padding: 5px 7px; text-align: left;
  vertical-align: top; }
th { background: var(--tint); font-weight: 600; }
td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; }
tfoot td { font-weight: 700; background: var(--tint); }
dl { display: grid; grid-template-columns: minmax(190px, auto) 1fr; gap: 3px 14px;
  margin: 6px 0; font-size: 10.5pt; }
dt { color: var(--muted); }
dd { margin: 0; }
.pending { color: #8a6d3b; font-style: italic; }
.warn { border-left: 3px solid #a8410f; background: #fbf1ec; padding: 7px 10px;
  margin: 8px 0; font-size: 10.5pt; }
.fail { border-left: 3px solid #8c1d18; background: #fbeceb; padding: 7px 10px;
  margin: 8px 0; font-size: 10.5pt; }
footer { margin-top: 22px; padding-top: 8px; border-top: 1px solid var(--line);
  color: var(--muted); font-size: 9.5pt; }
@media print { body { padding: 0; } h2 { break-after: avoid; }
  table, dl { break-inside: avoid; } }
`;

/**
 * How far the text must stay clear of the letterhead artwork, in millimetres.
 *
 * MEASURED OFF THE IMAGE. Its header band ends about 19.5% down and the curve
 * dips lower on the right, which on A4 is close to 60mm; 68mm leaves a clear
 * margin under it rather than a hairline. The strapline begins about 93.5%
 * down, so 30mm from the foot.
 *
 * NOT PAGE MARGINS -- in-flow spacing. See `documentShell` for why it has to
 * be the one and cannot be the other.
 */
export const LETTERHEAD_MARGIN_MM = {
  top: 68,
  right: 25,
  bottom: 30,
  left: 25,
} as const;

/** The class naming the full-sheet letterhead. `pdfMarginFor` looks for it. */
const LETTERHEAD_CLASS = "letterhead-sheet";

/**
 * Marks where a letterheaded document's content ends, INSIDE the repeating
 * page frame.
 *
 * `renderSignedPdf` appends its certificate of signature to a rendered
 * document. Appended after `</body>` it lands outside that frame, and the
 * certificate pages print with no clearance at all -- the first thing a
 * customer sees on the evidence page is a heading laid across the company
 * logo. Splitting here puts the certificate inside the frame, where it is
 * paginated with the same clearance as everything else.
 */
export const DOCUMENT_BODY_END = "<!--sts:body-end-->";

export interface PdfMarginMm {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

/** A letterheaded document is printed edge to edge; its frame does the spacing. */
const NO_MARGIN: PdfMarginMm = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * The margin a rendered document must be printed at.
 *
 * ZERO for a letterheaded document, and that is the whole point rather than an
 * oversight: the artwork runs to all four edges of the paper, and Chromium
 * will not paint anything into a page margin -- an element positioned out
 * there is simply clipped away. So the page has no margin, the sheet covers
 * it, and the clearance comes from the frame in `documentShell`.
 *
 * Reads the answer off the markup rather than asking the caller to track it,
 * because the two call sites that PDF these documents -- the report route and
 * the signed-agreement renderer -- both reach them as HTML and neither has the
 * header to hand by then. Getting this wrong is silent: the PDF renders, and
 * the branding is printed through the words.
 */
export function pdfMarginFor(html: string, fallback: PdfMarginMm): PdfMarginMm {
  return html.includes(`class="${LETTERHEAD_CLASS}"`) ? NO_MARGIN : fallback;
}

export function documentShell(opts: {
  title: string;
  standardsRef: string;
  header: DocumentHeader;
  body: string;
  footer: string;
}): string {
  const { header } = opts;
  const meta: string[] = [
    `<span><b>Job</b> ${esc(header.jobReference)}</span>`,
    `<span><b>Property</b> ${esc(header.propertyAddress)}</span>`,
    `<span><b>Issued</b> ${esc(isoDateToLong(header.issuedOn))}</span>`,
  ];
  if (header.preparedBy) {
    meta.push(`<span><b>Prepared by</b> ${esc(header.preparedBy)}</span>`);
  }
  if (header.mcsNumber) {
    meta.push(`<span><b>MCS</b> ${esc(header.mcsNumber)}</span>`);
  }

  /*
   * THE LETTERHEAD, ON EVERY PAGE.
   *
   * A full-sheet A4 image rather than a logo in the corner, because that is
   * what the company's own template is: one image behind the text, a header
   * band at the top and a strapline at the foot.
   *
   * THREE THINGS HAVE TO BE TRUE AT ONCE, and each rules out the obvious way
   * of getting the other two. They were worked out by printing the candidates
   * and looking at them, not from the specification.
   *
   * 1. The artwork reaches all four edges of the paper, so THE PAGE MUST HAVE
   *    NO MARGIN. Chromium will not paint into a page margin: an element
   *    positioned out there is clipped away, and a background offset into it
   *    simply disappears. With margins set, the header band and strapline
   *    never appear at all.
   *
   * 2. So the sheet is a FIXED-POSITION ELEMENT covering the whole page.
   *    Chromium repeats a fixed element on every printed page. A background on
   *    <body> does not: it is painted once over a box that spans every page,
   *    so `background-repeat` tiled every 297mm while the content advanced by
   *    one page's content height. That was the bug this replaces -- the
   *    artwork climbed about 34mm up the paper on each successive page, and by
   *    page four of a signed agreement the header band and the strapline were
   *    printed across the middle of the text. `background-attachment: fixed`
   *    does not rescue it; that was tried and drifts the same way.
   *
   * 3. The text must then keep clear of the artwork ON EVERY PAGE, and with no
   *    page margin to do it. PADDING CANNOT: it applies once to the element,
   *    holding the first page's head and the last page's foot clear and
   *    nothing in between, which is why pages two and three had text hard
   *    against the paper edge. A table's `thead` and `tfoot` can -- Chromium
   *    repeats them on every page and reserves their height there. So the
   *    document sits in a one-cell table whose head and foot are empty spacers
   *    of exactly the clearances above.
   *
   * Absent for an organisation with no letterhead, and then none of this is
   * emitted at all -- the document keeps the plain margins it always had.
   */
  const m = LETTERHEAD_MARGIN_MM;
  const paper = header.letterhead
    ? `@page { size: A4; margin: 0; }
.${LETTERHEAD_CLASS} {
  position: fixed;
  top: 0;
  left: 0;
  width: 210mm;
  height: 297mm;
  background-image: url("${header.letterhead}");
  background-size: 210mm 297mm;
  background-repeat: no-repeat;
  z-index: -1;
  /* Chromium drops background imagery from print unless told otherwise, and
     a letterhead that vanishes in the PDF is the same failure upside down. */
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}
/* The repeating frame. This table is structure, not a table, so everything the
   document stylesheet gives a real one is taken back off here -- borders,
   padding, margins and above all BACKGROUND. A tfoot cell is tinted for a
   totals row, and left alone it paints a pale block straight over the
   strapline that the frame exists to keep clear. */
table.sts-page { width: 100%; border-collapse: collapse; margin: 0; font-size: inherit; }
table.sts-page > thead > tr > td,
table.sts-page > tfoot > tr > td,
table.sts-page > tbody > tr > td {
  padding: 0; border: 0; background: none; font-weight: inherit;
}
table.sts-page > thead > tr > td { height: ${m.top}mm; }
table.sts-page > tfoot > tr > td { height: ${m.bottom}mm; }
table.sts-page > tbody > tr > td {
  padding: 0 ${m.right}mm 0 ${m.left}mm; vertical-align: top;
}
@media screen {
  body { max-width: 210mm; margin: 0 auto; padding: 0;
    box-shadow: 0 1px 12px rgba(0,0,0,0.14); }
  /* One sheet behind a continuous page: on screen there is no pagination to
     repeat it against, and a fixed sheet would follow the reader's scroll. */
  .${LETTERHEAD_CLASS} { position: absolute; top: 0; left: 0; }
}`
    : "";
  const sheet = header.letterhead
    ? `<div class="${LETTERHEAD_CLASS}" aria-hidden="true"></div>\n`
    : "";
  const frameOpen = header.letterhead
    ? `<table class="sts-page"><thead><tr><td></td></tr></thead>\n` +
      `<tfoot><tr><td></td></tr></tfoot><tbody><tr><td>\n`
    : "";
  const frameClose = header.letterhead ? `\n</td></tr></tbody></table>` : "";

  return `<!DOCTYPE html>
<html lang="en-GB">
<head>
<meta charset="utf-8">
<title>${esc(opts.title)}</title>
<style>${STYLES}
${paper}</style>
</head>
<body>
${sheet}${frameOpen}<header class="doc-header">
<h1>${esc(opts.title)}</h1>
<p class="standard">${esc(opts.standardsRef)}</p>
<div class="doc-meta"><span><b>${esc(header.organisationName)}</b></span>${meta.join("")}</div>
</header>
${opts.body}
<footer>${opts.footer}</footer>
${DOCUMENT_BODY_END}${frameClose}
</body>
</html>`;
}
